'use client';

import { ArrowUpFromDotIcon, ExternalLinkIcon, PlusIcon, SaveIcon } from 'lucide-react';

import { VoiceAgentPromptPreview } from '@/app/(app)/admin/agent/voice/[id]/voice-agent-blocks';
import { PageMain } from '@/app/(app)/page-main';
import { ActionsSettings } from '@/app/(app)/voice-agent/tab-actions';
import { BehaviorSettings } from '@/app/(app)/voice-agent/tab-behaviors';
import { NewCustomerCreationSettings } from '@/app/(app)/voice-agent/tab-customer-creation';
import { VoiceAgentGeneral } from '@/app/(app)/voice-agent/tab-general';
import { Link } from '@/components/link';
import { useAuthCtx } from '@/components/providers/auth-provider';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useVoiceSettingsCtx, VoiceSettingsProvider } from './provider';
import { ExistingCustomerSettings } from './tab-customer-existing';
import { NotificationsSettings } from './tab-notifications';
import { CallRoutingSettings } from './tab-routing';

export function VoiceAgentPage() {
  return (
    <VoiceSettingsProvider>
      <VoiceAgentContent />
    </VoiceSettingsProvider>
  );
}

function VoiceAgentContent() {
  const { isSuperuser } = useAuthCtx();
  const { agent, tab, setTab, isChanged, isSaveLoading, onSave } = useVoiceSettingsCtx();
  return (
    <PageMain
      title='Voice AI'
      breadcrumbs={[
        {
          label: 'Settings',
          href: '/settings',
        },
      ]}
      right={
        <div className='flex items-center gap-4'>
          {isChanged && <div className='text-sm font-semibold text-destructive'>Unsaved changes</div>}
          <Button onClick={onSave} disabled={!isChanged || isSaveLoading} size='sm'>
            <SaveIcon className='mr-1.5 size-4' />
            Save
          </Button>
          {isSuperuser && <CreateOrDeployAgentButton />}
          {isSuperuser && <ViewAgentButton />}
          {isSuperuser && agent && <VoiceAgentPromptPreview agentId={agent.id} />}
        </div>
      }
    >
      <Tabs value={tab} onValueChange={(value) => setTab(value as typeof tab)}>
        <TabsList className='mt-3 mb-2 grid grid-cols-7'>
          <TabsTrigger value='general'>General</TabsTrigger>
          <TabsTrigger value='existing_customer'>Existing Customer</TabsTrigger>
          <TabsTrigger value='new_customer'>New Customer</TabsTrigger>
          <TabsTrigger value='actions'>Actions</TabsTrigger>
          <TabsTrigger value='behaviors'>Behaviors</TabsTrigger>
          <TabsTrigger value='call_routing'>Call Routing</TabsTrigger>
          <TabsTrigger value='notifications'>Notifications</TabsTrigger>
        </TabsList>
        <TabsContent value='general'>
          <VoiceAgentGeneral />
        </TabsContent>
        <TabsContent value='existing_customer'>
          <ExistingCustomerSettings />
        </TabsContent>
        <TabsContent value='new_customer'>
          <NewCustomerCreationSettings />
        </TabsContent>
        <TabsContent value='call_routing'>
          <CallRoutingSettings />
        </TabsContent>
        <TabsContent value='behaviors'>
          <BehaviorSettings />
        </TabsContent>
        <TabsContent value='actions'>
          <ActionsSettings />
        </TabsContent>
        <TabsContent value='notifications'>
          <NotificationsSettings />
        </TabsContent>
      </Tabs>
    </PageMain>
  );
}

function CreateOrDeployAgentButton() {
  const { agent, onCreateAgent, isCreateAgentLoading, onPublishAgent, isPublishAgentLoading } = useVoiceSettingsCtx();

  if (agent) {
    return (
      <Button onClick={onPublishAgent} disabled={isPublishAgentLoading} size='sm'>
        <ArrowUpFromDotIcon className='mr-1.5 size-4' />
        Deploy
      </Button>
    );
  }

  return (
    <Button onClick={onCreateAgent} disabled={isCreateAgentLoading} size='sm'>
      <PlusIcon className='mr-1.5 size-4' />
      Create Agent
    </Button>
  );
}

function ViewAgentButton() {
  const { agent } = useVoiceSettingsCtx();

  if (!agent) return null;
  if (!agent.retell_agent_id) return null;

  return (
    <Button variant='outline' size='sm' asChild>
      <Link
        href={`https://dashboard.retellai.com/agents/${agent.retell_agent_id}`}
        target='_blank'
        rel='noopener noreferrer'
      >
        <ExternalLinkIcon className='mr-1.5 size-4' />
        View on Retell
      </Link>
    </Button>
  );
}
